import React, { useEffect, useMemo, useReducer, useState } from 'react';
import { InputWrapper } from '.'
import {
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material'
import { useMUITheme } from "../../../theme/ThemeProvider";
import { InlineAlert } from "common/alerts";
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs'
import { AdvancedSearchAction, SavedSearchActionTypes } from '../../../customEnums/index';
import { CreateSearchProps } from 'interfaces/pages/components';
import { TransactionSummaryStyles } from '../../../customStyles/index';
import { AlertStyles } from '../../../customStyles/common';
import { AlertColor } from '../../../customEnums/AlertColor';
import { useTableStore } from 'state/TableStore';
import { getCurrentEnv } from "../../../utils";
import { States } from 'interfaces/common';
import { useUserStore } from 'state';
import { savedSearchesReducer } from "../../../pages/TransactionSummary/helpers/savedSearchesReducer";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { extractPageName } from 'common/CustomTable/helpers';
import { useLocation } from 'react-router-dom';


enum ModalActionTypes {
  CHANGE_LABEL_NAME = "CHANGE_LABEL_NAME",
}

const initialModalState = {
  labelName: ""
}

const modalReducer = (currentState: any, { type, payload }: any) => {

  switch (type) {
    case ModalActionTypes.CHANGE_LABEL_NAME:
      const { labelName } = payload;
      return {
        ...currentState,
        labelName
      };
    default: throw new Error('Invalid action type');
  }
};

export const AdvancedSearch: React.FC<CreateSearchProps> = (
  { filters,
    formState,
    setFormValues,
    handleRunSearch,
    setCreateSearch,
    setSearchLength,
    savedSearchState,
    postedConfig,
    displaySavedMessage,
    setDisplaySavedMessage,
    updatingConfig,

  }) => {
  const { mode } = useMUITheme();
  const isDark = mode === 'dark';
  const [modalState, dispatch] = useReducer(modalReducer, initialModalState);
  const { labelName } = modalState;
  const [empty, setEmpty] = useState(true);
  const configService = useTableStore(state => state.configService)
  const setConfigService = useTableStore(state => state.setConfigService)
  const setIsDelete = useTableStore((state) => state.setIsDelete)
  const setConfigFile = useTableStore((state) => state.setConfigFile)
  const setConfigBody = useTableStore((state) => state.setConfigBody)
  const setIsPost = useTableStore((state) => state.setIsPost)
  const setInternalSearchArray = useTableStore(state => state.setInternalSearchArray)
  const STATE = getCurrentEnv
  const config = useUserStore(state => state.config)
  const { softLimit, hardLimit, } = config.transactionViewing
  const userConfig = useUserStore(state => state.userConfig)
  const { isAdmin } = userConfig
  const { searchData } = configService
  const loadedData = useMemo(() => ({ ...searchData }), [searchData]);
  const [, dispatchSearch] = useReducer(savedSearchesReducer, loadedData);
  const zoomLevel = useTableStore(state => state.zoomLevel);
  const locationObj = useLocation()
  const pageName = extractPageName(locationObj.pathname)

  dayjs.extend(isSameOrBefore);


  // Display success message when posting config, defined in the config service hook, is set to true
  useEffect(() => {
    if (postedConfig) {
      setDisplaySavedMessage(postedConfig)
    }
  }, [postedConfig, setDisplaySavedMessage]);

  const onLabelNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({ type: ModalActionTypes.CHANGE_LABEL_NAME, payload: { labelName: e.target.value } });
    if (e.target.value.trim().length < 1) {
      setDisplaySavedMessage(false)
    }
  };

  const handleSavedSearch = async () => {
    dispatchSearch(
      {
        type: SavedSearchActionTypes.ADD_SAVED_SEARCH,
        savedSearch: {
          label: labelName,
          searchParams: formState[0]
        },
        setters: {
          setIsDelete,
          setConfigFile,
          setConfigBody,
          setIsPost,
          setInternalSearchArray,
          getDashboardPreferences: () => useTableStore.getState().configService.dashboardPreferences ?? {}
        }
      }
    );
    setConfigService({ ...configService, postedConfig: false })
    setFormValues({});
  };

  const { firstName, lastName, status, tot, lowerDate, upperDate, rfp, stateRFPCode, tcn } = formState[0]
  const dispatchFormValues = formState[1]

  useEffect(() => {
    const searchParams = formState[0]
    const searchParamsKeys = Object.keys(searchParams)
    const textFieldsEmpty = searchParamsKeys.filter(key => !['days', 'lowerDate', 'upperDate'].includes(key)).every(key => {
      const value = searchParams[key];
      return typeof value === 'string' ? value.trim() === '' : !value;
    })
    const dateFieldsEmpty = !lowerDate && !upperDate;
    setEmpty(textFieldsEmpty && dateFieldsEmpty)
  }, [formState, lowerDate, savedSearchState, upperDate])

  let statusTypes = filters.find((filter) => filter.filterColumn === "status")!.filterValues

  const validateDateRangeFields = () => {
    if (!lowerDate && !upperDate) return true
    if (!lowerDate || !upperDate) return false

    const lowerValid = lowerDate ? dayjs(lowerDate, 'MM/DD/YYYY').isValid() : true;
    const upperValid = upperDate ? dayjs(upperDate, 'MM/DD/YYYY').isValid() : true;

    return lowerValid && upperValid
  }
  const isValidDateRange = validateDateRangeFields();
  const isOver120Days = useMemo(() => upperDate && lowerDate && Math.abs(upperDate.diff(lowerDate, "day")) > 120, [upperDate, lowerDate]);

  return (
    <Dialog fullWidth={true} open={true} PaperProps={{ sx: { ...TransactionSummaryStyles.createSearchDialog, transform: `scale(${1 / zoomLevel})` } }} data-testid="advanced-search-modal">
      <DialogTitle>
        <Typography variant='h1' sx={{ fontSize: "1.125rem" }} color='text.primary'>
          SEARCH PARAMETERS

        </Typography>
      </DialogTitle>
      <DialogContent sx={TransactionSummaryStyles.createSearchDialogContent}>
        <Typography variant='body1' sx={{ width: "100%", height: "40px", mb: '0.75rem' }}>
          Define one or more search parameters and select 'Run Search'. Otherwise, save  your search as a template or exit without searching.
        </Typography>
        <InputWrapper>
          <TextField
            label="First Name"
            aria-label="First name"
            aria-description='Enter the first name to search'
            name="firstName"
            data-testid="firstname-text-field"
            value={firstName}
            placeholder="First Name"
            variant="outlined"
            sx={TransactionSummaryStyles.createSearchTextField}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => dispatchFormValues({ type: AdvancedSearchAction.FIRST_NAME, value: e.target.value })}
          />
          <TextField
            label="Last Name"
            aria-label="Last name"
            aria-description='Enter the last name to search'
            name="lastName"
            data-testid="lastname-text-field"
            value={lastName}
            placeholder="Last Name"
            variant="outlined"
            sx={TransactionSummaryStyles.createSearchTextField}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => dispatchFormValues({ type: AdvancedSearchAction.LAST_NAME, value: e.target.value })}
          />
        </InputWrapper>

        <InputWrapper>
          <FormControl>
            <InputLabel color="secondary" id="status-label">Status</InputLabel>
            <Select
              value={status || ''}
              id="status-select"
              labelId='status-label'
              onChange={(e: any) => dispatchFormValues({ type: AdvancedSearchAction.STATUS, value: e.target.value })}
              variant="outlined"
              data-testid="status-select"
              sx={TransactionSummaryStyles.createSearchTextField}
              label="Status"
              aria-label="Status"
              aria-description='Select the status to search'>
              <MenuItem value="" data-testid="clear-status-menu-item">
                <em>&nbsp;</em>
              </MenuItem>
              {
                Object.keys(statusTypes).map((key) => {
                  return (
                    <MenuItem
                      value={statusTypes[key].label}
                      data-testid={`${statusTypes[key].label.toLowerCase().replace(/ /g, '-')}-menu-item`}
                      key={key}
                    >
                      {statusTypes[key].label}
                    </MenuItem>
                  )
                })
              }
            </Select>
          </FormControl>
          <FormControl>
            <InputLabel id="transaction-type-label">Transaction Type</InputLabel>
            <Select
              value={tot || ''}
              id='transactionType'
              labelId="transaction-type-label"
              aria-label="Transaction Type"
              aria-description='Select the Transaction Type to search'
              onChange={(e: any) => dispatchFormValues({ type: AdvancedSearchAction.TYPE, value: e.target.value })}
              sx={TransactionSummaryStyles.createSearchTextField}
              label="Transaction Type"
              data-testid="transaction-type-select"
            >
              <MenuItem value="" data-testid="clear-transaction-type-meu">
                <em>&nbsp;</em>
              </MenuItem>
              {
                config.pages[pageName].advancedSearch.transactionTypes.map((type) => {
                  return <MenuItem value={type} data-testid={`${type.toLowerCase().replace(/ /g, '-')}-menu-item`} key={type}>{type}</MenuItem>
                })
              }
            </Select>
          </FormControl>
        </InputWrapper>
        <InputWrapper>
          <DatePicker
            aria-label="Lower date"
            aria-description='Select the lower date to search'
            maxDate={upperDate || dayjs()}
            value={lowerDate}
            label={`Lower date ${upperDate ? 'required' : ''} `}
            slotProps={{
              textField: {
                placeholder: "MM / DD / YYYY",
                required: upperDate,
              },
            }}
            sx={{
              ...TransactionSummaryStyles.createSearchTextField,
              ...(upperDate && !lowerDate) && {
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'red'
                  }
                }
              }
            }}
            onChange={(newValue) => dispatchFormValues({ type: AdvancedSearchAction.LOWER_DATE, value: newValue })}
          />
          <DatePicker
            aria-label="Upper date"
            aria-description='Select the upper date to search'
            minDate={lowerDate}
            maxDate={lowerDate && dayjs(lowerDate).add((isAdmin === 'true') ? hardLimit : softLimit, 'days').isSameOrBefore(dayjs())
              ? dayjs(lowerDate).add((isAdmin === 'true') ? hardLimit : softLimit, 'days')
              : dayjs()
            }
            value={upperDate}
            label={`Upper date ${lowerDate ? 'required' : ''} `}
            slotProps={{
              textField: {
                placeholder: "MM / DD / YYYY",
                required: lowerDate,
              },
            }}
            sx={{
              ...TransactionSummaryStyles.createSearchTextField,
              ...(lowerDate && !upperDate) && {
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'red'
                  }
                }
              }
            }}
            onChange={(newValue) => dispatchFormValues({ type: AdvancedSearchAction.UPPER_DATE, value: newValue })}
          />
        </InputWrapper>
        {
          lowerDate &&
          <Container disableGutters sx={TransactionSummaryStyles.createSearchSwitchContainer}>
            <Typography sx={{ display: 'flex', flexDirection: "row" }}>
              Records can be searched within a
              &nbsp;
              <Typography color='#430099'>{`${(isAdmin === 'true') ? hardLimit : softLimit} day`}</Typography>
            </Typography>
            &nbsp; range.
          </Container>
        }
        <FormControl sx={{ flexGrow: 1, height: "56px" }}>
          <InputWrapper>
            <TextField
              aria-label={STATE === States.GA ? "Statute" : "Reason Fingerprinted"}
              aria-description={STATE === States.GA ? "Select the Statute to search" : "Select the Reason Fingerprinted to search"}
              label={
                (STATE === "ga" || STATE === "mc")
                  ? "Statute"
                  : "Reason Fingerprinted"
              }
              name="reasonFingerprinted"
              data-testid="reason-finger-printed-text-field"
              value={
                (STATE === "ga" || STATE === "mc")
                  ? stateRFPCode
                  : rfp
              }
              onChange={(e: any) => dispatchFormValues({ type: AdvancedSearchAction.RFP, value: e.target.value })}
              placeholder="Reason Fingerprinted"
              variant="outlined"
              sx={TransactionSummaryStyles.createSearchTextField}
            >
            </TextField>

            <TextField
              label="TCN"
              aria-label="TCN"
              aria-description='Enter the TCN to search'
              name="tcn"
              data-testid="tcn-text-field"
              value={tcn}
              placeholder="TCN"
              variant="outlined"
              sx={TransactionSummaryStyles.createSearchTextField}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => dispatchFormValues({ type: AdvancedSearchAction.TCN, value: e.target.value })}
            />
          </InputWrapper>
        </FormControl>
        <Divider aria-hidden role="separator" />

        <Container disableGutters sx={{ display: "flex", flexDirection: "column", justifyContent: "space-evenly", height: "61px", width: "100%" }} data-testid="save-your-search-container">
          <Typography sx={{ width: "100%" }} variant="h6" color="text.primary">SAVE YOUR SEARCH</Typography>
          <Typography sx={{ width: "100%" }} variant='body1'>Save your search as a template. Search templates will be added to the 'Search' field.</Typography>
        </Container>

        {displaySavedMessage && (
          <InlineAlert
            severity={AlertColor.SUCCESS}
            title="Search Saved!"
            message='This saved search has been added to the "Search" field.'
            sx={{ ...AlertStyles.success, width: '100%', display: 'flex' }}
          />
        )}


        <TextField
          aria-label="Saved search Name"
          aria-description='Inform a name to save the search'
          label="Saved Search Name"
          data-testid="saved-search-name-text-field"
          sx={{ height: "56px", width: "100%" }}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onLabelNameChange(e)}
          placeholder="Saved search name"
        />
        <Button
          disabled={labelName.trim().length === 0 || empty || isOver120Days || !isValidDateRange
            ? true
            : updatingConfig || labelName.trim().length === 0
              ? true
              : false}
          onClick={() => { handleSavedSearch(); }}
          data-testid="save-search-button"
          variant="contained"
          color='secondary'
          sx={TransactionSummaryStyles.createSearchSaveButton}
          aria-label='save search'>
          {updatingConfig ? <CircularProgress size='1.5rem' sx={{ color: '#fff' }} /> : "Save Search"}
        </Button>
      </DialogContent>
      <DialogActions sx={{ p: '1rem 1.5rem' }}>
        <Button
          onClick={() => {

            setConfigService({ ...configService, postedConfig: false })
            setCreateSearch(false);
            setSearchLength(0);
            setFormValues({});
            setDisplaySavedMessage(false);
            dispatchFormValues({ type: AdvancedSearchAction.RESET, hardLimit });
          }}
          data-testid="cancel-button"
          variant="contained"
          color='secondary'
          sx={{ ...TransactionSummaryStyles.createSearchButtons, color: isDark ? '#fff' : null }}>
          CANCEL
        </Button>
        <Button
          disabled={empty || !isValidDateRange || isOver120Days}
          onClick={() => handleRunSearch()}
          data-testid="run-search-button"
          variant="contained"
          color='secondary'
          sx={{ ...TransactionSummaryStyles.createSearchButtons, width: "138px" }}
        >
          RUN SEARCH
        </Button>
      </DialogActions>
    </Dialog>
  )
}
